/**
 * Auth Error Transformer
 * 
 * Transforms technical Supabase authentication errors into student-friendly messages
 * that are less alarming and more actionable for younger users.
 */

export interface AuthErrorMessage {
  title: string;
  description: string;
}

/**
 * Helpful password suggestions using Australian context
 */
const PASSWORD_TIPS = [
  "purpleKangaroo42!",
  "sunnyWombat2024",
  "TassieDevil_Rocks",
  "koalaDreams99!",
  "beachSunset_123",
  "mountainHike2025",
  "starryNight_42",
  "rainbowBridge99",
];

/**
 * Get a random password tip
 */
function getRandomPasswordTip(): string {
  return PASSWORD_TIPS[Math.floor(Math.random() * PASSWORD_TIPS.length)];
}

/**
 * Transforms a Supabase auth error into a student-friendly message
 */
export function getAuthErrorMessage(error: Error | { message: string } | null): AuthErrorMessage {
  if (!error || !error.message) {
    return {
      title: "Something went wrong",
      description: "Please try again. If this keeps happening, contact support.",
    };
  }

  const message = error.message.toLowerCase();

  // HIBP (Have I Been Pwned) password rejection
  if (message.includes("data breach") || message.includes("leaked") || message.includes("pwned")) {
    return {
      title: "Password not secure enough",
      description: `This password has been used by others and could be easily guessed. Try creating something unique - maybe combine a few random words like '${getRandomPasswordTip()}'`,
    };
  }

  // Weak password (too short, missing requirements)
  if (message.includes("weak password") || message.includes("password is too weak")) {
    return {
      title: "Password needs to be stronger",
      description: "Your password needs more variety. Try mixing letters, numbers, and symbols - like 'sunnyDay_2025!'",
    };
  }

  // Password too short
  if (message.includes("at least") && message.includes("character")) {
    return {
      title: "Password too short",
      description: "Your password needs to be at least 6 characters long. Try making it a bit longer!",
    };
  }

  // User already exists
  if (message.includes("already registered") || message.includes("already exists") || message.includes("user already")) {
    return {
      title: "Account already exists",
      description: "Looks like you already have an account with this email. Try signing in instead!",
    };
  }

  // Invalid login credentials
  if (message.includes("invalid login credentials") || message.includes("invalid credentials")) {
    return {
      title: "Couldn't sign you in",
      description: "The email or password doesn't match our records. Double-check and try again.",
    };
  }

  // Email not confirmed
  if (message.includes("email not confirmed") || message.includes("confirm your email")) {
    return {
      title: "Please confirm your email",
      description: "Check your inbox for a confirmation link before signing in.",
    };
  }

  // Rate limiting
  if (message.includes("too many requests") || message.includes("rate limit")) {
    return {
      title: "Too many attempts",
      description: "Please wait a moment before trying again.",
    };
  }

  // Network/connection errors
  if (message.includes("network") || message.includes("connection") || message.includes("fetch")) {
    return {
      title: "Connection problem",
      description: "Check your internet connection and try again.",
    };
  }

  // Invalid email format
  if (message.includes("invalid email") || message.includes("email format")) {
    return {
      title: "Invalid email address",
      description: "Please check that your email address is correct.",
    };
  }

  // Default fallback - use original message but with friendlier title
  return {
    title: "Something went wrong",
    description: error.message,
  };
}
